import {t} from '../../i18n'
import {useTextWidth} from '@tag0/use-text-width';

import './Preview.scss'
import OneThird from "../layouts/OneThird";
import ScrollingText from "../components/ScrollingText";
import {DisplayConfig} from "../Config";
import {
    DisplayCommand,
    MatchAlliance,
    MatchTeam,
    PlayoffType,
    ShowPreview,
    ShowSetup,
    TournamentLevel
} from "../../javaTypes";
import CmpBar, {truncateMatchNameForCmpBar} from "../layouts/CmpBar";
import DisplayParams from "../DisplayParams";
import {ReactNode, useContext} from "react";
import MiniBracket from "../components/MiniBracket";
import {bracketTargets, isDoubleElimsSeriesDescription} from "../util";
import {RankingData} from "../rankingDataState";
import PrimaryMatch from "../layouts/PrimaryMatch";
import {Timeout} from "../displayContainer";
import MiniTimeout, {MiniTimeoutType} from "../layouts/MiniTimeout";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import FormattedSeed from "../components/FormattedSeed";
import EventLogo from "../components/EventLogo";

function PreviewBar({color, slot, team, rank, leagueRank, showLeagueRankings, fontRange, maxWidth, oneLine} : {
    color: 'red' | 'blue' | 'orange',
    slot: number,
    team: MatchTeam,
    rank: string | number | null,
    leagueRank: string | number | null,
    showLeagueRankings?: boolean,
    fontRange: [number, number],
    maxWidth: number,
    oneLine?: boolean
}) {
    const textWidth = useTextWidth({text: team.name, font: `700 48px / 67px Tahoma`});
    const size = Math.max(fontRange[0], Math.min(fontRange[1], fontRange[1] * maxWidth / textWidth));
    if(oneLine) {
        return <div className={`previewBar previewBar_${color} previewBar_${slot}`}>
            <div className="previewTeamNumber previewItem">
                <div className={`team-avatar team-${team.number}`}></div>
                {team.number}</div>
            <div className={`previewTeamName previewItem`} style={{"fontSize": size}}>
                <ScrollingText>{team.name}</ScrollingText></div>
            {showLeagueRankings && leagueRank ?
                <div
                    className="previewTeamLeagueRank previewItem">{leagueRank ? `LR: ${leagueRank}` : ''}</div> : null}
            {rank ? <div className="previewTeamRank previewItem">{rank}</div> : null}
        </div>
    } else {
        return <div className={`previewBar previewBar_${color} previewBar_${slot}`}>
            <div className={"previewTeamHeader"}>
                <div className="previewTeamNumber previewItem">
                    <div className={`team-avatar team-${team.number}`}></div>
                    {team.number}</div>
                {showLeagueRankings && leagueRank ?
                    <div
                        className="previewTeamLeagueRank previewItem">{leagueRank ? `LR: ${leagueRank}` : ''}</div> : null}
                {rank ? <div className="previewTeamRank previewItem">{rank}</div> : null}
            </div>
            <div className={"previewTeamMain"}>
                <div className={`previewTeamName previewItem`} style={{"fontSize": size}}>
                    <ScrollingText>{team.name}</ScrollingText></div>
            </div>
        </div>
    }
}

function PreviewSet({color, alliance, shortTitle, elims, showLeagueRankings, hideRanking, maxWidth, overlay, isDoubleElims}: {
    color: 'red' | 'blue' | 'orange',
    alliance: Partial<MatchAlliance<MatchTeam>>,
    shortTitle: boolean,
    elims: boolean,
    showLeagueRankings?: boolean,
    hideRanking?: boolean,
    maxWidth: number,
    overlay?: boolean,
    isDoubleElims?: boolean,
}) {
    let teams = !alliance.teams ? [] : alliance.teams;
    teams = teams.filter(t => t.number != "")
    const fontRange : [number, number] = overlay ? (teams.length > 3 ? [30, 30] : [32, 36]) : [32, 48];
    const items = teams.map((team, i) => {
        let rank : string | number | null = hideRanking ? '' : team.ranking < 0 ? t`NP` : (team.ranking === 0 ? '' : team.ranking);
        let leagueRank = showLeagueRankings ? (team.leagueRanking < 0 ? t`NP` : team.leagueRanking) : null;
        if (elims && alliance.seed) {
            rank = null;
            leagueRank = null;
        }
        return <PreviewBar key={i} color={color} slot={i + 1} team={team} rank={rank} showLeagueRankings={showLeagueRankings} leagueRank={leagueRank} fontRange={fontRange} maxWidth={maxWidth} oneLine={overlay || isDoubleElims}/>;
    })

    let rankElement = null;
    if(elims && teams[0] && teams[0].ranking != 0) {
        rankElement = <span className={"allianceRank"}>{t`Rank `}{teams[0].ranking < 0 ? t`NP` : <>{teams[0].ranking} <span className={"text-nowrap"}>({alliance.wins}-{alliance.losses}-{alliance.ties})</span></>}</span>;
    }

    return <div className={`previewSet n-teams-${teams.length}`} id={`previewSet_${color}`}>
        {elims && alliance.seed ? <div className={`allianceNameWrapper ${rankElement ? 'withRank' : ''}`}><span className={"allianceName"}><FormattedSeed seed={alliance.seed} compact={overlay}/></span> {rankElement}</div> : ''}
        {items}
    </div>;
}

function DoubleEliminationTargets({ series }: { series: number }) {
    const displayParams = useContext(DisplayParams);
    const rankingData = useContext(RankingData);
    const structure = rankingData[displayParams.divisionNumber || 0]?.playoffStructure;
    const thisSeries = structure?.series.find(s => s.series == series);
    const [winnerTarget, loserTarget] = bracketTargets(structure, series)
    const winnerTargetName = winnerTarget ? winnerTarget.name.replace(/^Playoff /, '') : <>1<sup>st</sup> Place</>;
    const loserTargetName = loserTarget ? loserTarget.name.replace(/^Playoff /, '') : t`Eliminated`;

    if(thisSeries?.isFinals && winnerTarget) {
        // This must be the first finals match, so show appropriate text

        return <div className={"deTarget"}>
            <span><span className={"redLabel"}>{t`Red`}</span> <i className={"fa fa-trophy"}></i> </span> <i className={"fa fa-arrow-right"}></i> <span>Red wins</span>
            <span><span className={"blueLabel"}>{t`Blue`}</span> <i className={"fa fa-trophy"}></i></span> <i className={"fa fa-arrow-right"}></i> <span>{loserTargetName}</span>
        </div>
    } else if (thisSeries?.isFinals) {
        return <div className={"deTarget"}>
            <span><i className={"fa fa-trophy"}></i> {t`Winner`}</span> <i className={"fa fa-arrow-right"}></i>
            <span>{winnerTargetName}</span>
            <span><i className={"fa fa-arrow-down"}></i> {t`Loser`}</span> <i className={"fa fa-arrow-right"}></i>
            <span><>2<sup>nd</sup> Place</></span>
        </div>
    }

    return <div className={"deTarget"}>
        <span><i className={"fa fa-trophy"}></i> {t`Winner`}</span> <i className={"fa fa-arrow-right"}></i>
        <span>{winnerTargetName}</span>
        <span><i className={"fa fa-arrow-down"}></i> {t`Loser`}</span> <i className={"fa fa-arrow-right"}></i>
        <span>{loserTargetName}</span>
    </div>
}

export default function Preview({ config, command, showLeagueRankings, missingLeague }: {
    config: DisplayConfig,
    command: DisplayCommand<ShowPreview | ShowSetup>,
    showLeagueRankings?: boolean,
    missingLeague?: boolean
}) {
    const displayParams = useContext(DisplayParams);
    const isPlayoff = command.params.tournamentLevel == TournamentLevel.PLAYOFF;
    const isSingleTeam = command.params.singleTeam;
    const isCmpBar = config.type == "cmpBar";
    const vflip = config.scoringBarLocation === "top";

    const overlay = config.overlay && (!('forceFullscreen' in command.params) || !command.params.forceFullscreen);
    const colors : ('red' | 'blue')[] = config.allianceOrientation === "flipped" ? ['blue', 'red'] : ['red', 'blue'];

    const state = useContext(RankingData);
    const structure = state[displayParams.divisionNumber || 0]?.playoffStructure;
    const isRoundRobin = structure?.type == PlayoffType.ROUND_ROBIN_WITH_FINALS;
    const seriesDescription = structure?.series.find(s => s.series == command.params.series);

    const isDoubleElims = seriesDescription ? isDoubleElimsSeriesDescription(seriesDescription) : false
    const maxWidth = overlay ? (isPlayoff ? 510 : 610) : (isDoubleElims ? 530 : 670);
    const sets = isSingleTeam ? [<PreviewSet key={"orange"} color="orange" overlay={overlay} isDoubleElims={isDoubleElims} shortTitle={isCmpBar} alliance={command.params.red} elims={false} maxWidth={maxWidth} />, null] :
        (colors).map(a => <PreviewSet key={a} color={a} overlay={overlay || isCmpBar} isDoubleElims={isDoubleElims} shortTitle={isCmpBar} alliance={command.params[a]} elims={isPlayoff && !(isCmpBar && displayParams.isDualDivisionParent)} showLeagueRankings={showLeagueRankings} hideRanking={missingLeague} maxWidth={maxWidth}/>)

    let elimsResults = null;
    if(isPlayoff && (!isRoundRobin || seriesDescription?.isFinals)) {
        elimsResults = <div className={"elimsSeriesResults"}>
            <div
                className={`wing left ${colors[0]}`}>{command.params[colors[0]].wins}</div>
            <div className={"resultKey"}>{t`Wins`}</div>
            <div
                className={`wing right ${colors[1]}`}>{command.params[colors[1]].wins}</div>
        </div>
    }

    const timeout = useContext(Timeout);
    const showTimeout = !isSingleTeam && timeout.timer && timeout.timer.remaining > 0;

    if(!overlay) {
        const Container = isCmpBar ? CmpBar : PrimaryMatch;
        let title: ReactNode = <><EventLogo logo={displayParams.matchPrefixLogo} /> {isCmpBar ? truncateMatchNameForCmpBar(command.params.matchName) : command.params.matchName}</>;
        let eventNameOverride: ReactNode = undefined
        let shortEventName = false
        if(isCmpBar && displayParams.isDualDivisionParent && isPlayoff) {
            // We assume that the CMP logo covers the event name adequately, and that the parent event doesn't have a designated sponsor and therefore
            // can use less space
            eventNameOverride = title;
            title = <><FormattedSeed seed={command.params[colors[0]].seed} compact /> vs <FormattedSeed seed={command.params[colors[1]].seed} compact /></>;
            shortEventName = true;
        }
        return <Container pageTitle={title}
                          pageName={t`Up Next`} config={config}
                          eventNameOverride={eventNameOverride} shortEventName={shortEventName}
                          className={`previewPage ${isSingleTeam ? 'singleTeam' : ''} ${isDoubleElims && !isCmpBar ? 'with-bracket' : ''} fullScreen`}>
            <div className={`fullPreview`}>
                {sets}
            </div>
            <div className={"previewVs"}>VS</div>
            {isPlayoff && !isCmpBar ? (isDoubleElims ? <MiniBracket divisionNumber={displayParams.divisionNumber} highlightSeries={command.params.series} /> : elimsResults) : null}
        </Container>;
    } else {
        return <div className={`h-100 w-100 ${isSingleTeam ? 'singleTeam' : ''} ${isPlayoff ? "elims" : ""} previewPage`}>
            <div className={`chroma ${vflip ? "vflip" : ""}`} style={{backgroundColor: `${config.overlayColor}`}}/>
            <TransitionGroup component={null}>
                {showTimeout ? <CSSTransition timeout={2000} classNames="mini-timeout-banner">
                    <MiniTimeout type={MiniTimeoutType.BANNER} config={config} timeout={timeout}/>
                </CSSTransition> : null}
            </TransitionGroup>
            <OneThird matchName={<><EventLogo logo={displayParams.matchPrefixLogo} />&nbsp;{command.params.matchName}</>} vflip={vflip}>
                {sets}
                {isPlayoff ? (isDoubleElims ? <DoubleEliminationTargets series={command.params.series} /> : elimsResults) : null}
            </OneThird>
        </div>
    }
}